import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { Box, ButtonBase, Rating, Slider, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { PbjStarThreshold } from "../../../utils/models";
import { brandBorder, brandWash, STAR_EMPTY } from "./colors";
import { ratingFromPoints } from "./stars.utils";

// Replaces the inline target-stars editor on the Stars banner. Lets admins pick
// a star tier (snaps the slider to that tier's lower bound) AND fine-tune the
// exact point goal in the same step. The committed value becomes the source of
// truth for the prediction's target — `stars` is recomputed from it on save.
type Props = {
	open: boolean;
	onClose: () => void;
	// `(targetPoints, derivedStars)` — caller owns persistence + recompute. The
	// derived star is sent so callers can avoid re-running the ladder lookup.
	onSave: (targetPoints: number, targetStars: number) => void;
	initialTargetPoints: number;
	maxPoints: number;
	starThresholds: PbjStarThreshold[];
	departmentId: string;
};

export const SetTargetDialog: FC<Props> = ({ open, onClose, onSave, initialTargetPoints, maxPoints, starThresholds, departmentId }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const theme = useTheme();
	const accent = theme.palette.primary.main;

	// Local working copy of the chosen point target. Reset whenever the dialog
	// reopens so cancel → reopen always shows the latest committed value, never
	// a stale draft from the previous session.
	const [localPoints, setLocalPoints] = useState(initialTargetPoints);
	useEffect(() => {
		if (open) setLocalPoints(initialTargetPoints);
	}, [open, initialTargetPoints]);

	const selectedStar = useMemo(() => ratingFromPoints(localPoints, starThresholds), [localPoints, starThresholds]);

	const handleSliderChange = (_: Event, value: number | number[]): void => {
		setLocalPoints(Array.isArray(value) ? value[0] : value);
	};

	const handleSave = (): void => {
		track("pbj_model_set_target_saved", {
			department_id: departmentId,
			target_points: localPoints,
			target_stars: selectedStar,
		});
		onSave(localPoints, selectedStar);
		onClose();
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			dataTestId="pbj-model-set-target-dialog"
			title={
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<TrackChangesIcon sx={{ fontSize: 20, color: accent }} />
					<Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
						{t("pbj_model.stars.set_target.dialog.title")}
					</Typography>
				</Box>
			}
			confirm={t("pbj_model.stars.set_target.save")}
			cancel={t("pbj_model.stars.set_target.cancel")}
			confirmButtonVariant="primary"
			onConfirm={handleSave}
			body={
				<Stack spacing={3} sx={{ pt: 1 }}>
					<Box>
						<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
							{t("pbj_model.stars.set_target.section.rating")}
						</Typography>
						<Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
							{starThresholds.map((tier) => {
								const isSelected = selectedStar === tier.star;
								return (
									<ButtonBase
										key={tier.star}
										onClick={() => setLocalPoints(tier.min)}
										data-testid={`pbj-model-set-target-star-${tier.star}`}
										sx={{
											flex: 1,
											minWidth: 72,
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											py: 1.5,
											px: 1,
											gap: 0.5,
											borderRadius: 1.5,
											border: "1px solid",
											borderColor: isSelected ? accent : brandBorder(theme),
											bgcolor: isSelected ? accent : "background.paper",
											color: isSelected ? "common.white" : "text.primary",
											transition: "background-color 120ms ease, color 120ms ease",
										}}
									>
										<Typography variant="h6" component="span" sx={{ fontWeight: 700, lineHeight: 1 }}>
											{tier.star}
										</Typography>
										<Rating
											value={tier.star}
											readOnly
											size="small"
											sx={{
												fontSize: 14,
												"& .MuiRating-iconFilled": { color: isSelected ? "common.white" : accent },
												"& .MuiRating-iconEmpty": { color: isSelected ? alpha(theme.palette.common.white, 0.4) : STAR_EMPTY },
											}}
										/>
									</ButtonBase>
								);
							})}
						</Stack>
					</Box>

					<Box>
						<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
							{t("pbj_model.stars.set_target.section.points")}
						</Typography>
						<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{t("pbj_model.stars.set_target.target_points_label")}
							</Typography>
							<Typography variant="h6" sx={{ fontWeight: 700, color: accent }}>
								{t("pbj_model.stars.set_target.points_unit", { points: localPoints })}
							</Typography>
						</Stack>
						<Slider
							value={localPoints}
							min={0}
							max={maxPoints}
							step={1}
							onChange={handleSliderChange}
							sx={{ color: accent }}
							data-testid="pbj-model-set-target-slider"
							aria-label={t("pbj_model.stars.set_target.target_points_label")}
						/>
					</Box>

					<Box
						sx={{
							p: 2,
							bgcolor: brandWash(theme),
							borderRadius: 2,
							border: "1px dashed",
							borderColor: brandBorder(theme),
							textAlign: "center",
						}}
					>
						<Typography variant="body2">
							<Trans
								i18nKey="pbj_model.stars.set_target.projection"
								values={{ points: localPoints, count: selectedStar }}
								components={{ bold: <Box component="strong" sx={{ color: accent }} /> }}
							/>
						</Typography>
					</Box>
				</Stack>
			}
		/>
	);
};
